import React from "react";

import {
  AutoscalingIcon,
  GuaranteedIcon,
  Link,
  WarningIcon
} from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import {
  deploymentSizingApiEnabledSelector,
  resourcesTypeSelector
} from "Reducers/environment/deployment";
import { selectFeatureAutoscalingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { MetricsDeploymentService } from "src/organization/pages/project/environment/metrics/containers/Service/type";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import { useAppSelector } from "Store/hooks";

import { DialogDescription } from "./style";

type ResourceInfoProps = {
  unit?: string;
  serviceKind?: string;
  service: MetricsDeploymentService;
  value?: Record<string, any>[];
};

const ResourceInfo = ({
  unit,
  value,
  serviceKind,
  service
}: ResourceInfoProps) => {
  const intl = useIntl();
  const location = useLocation();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const isSizingApiEnabled = useSelectorWithUrlParams(
    deploymentSizingApiEnabledSelector
  );

  const resourcesType = useSelectorWithUrlParams(resourcesTypeSelector);
  const { thresholdsEnabled } = useAutoscallingValues(service?.name);

  const autoscalingEnabledFlag = useAppSelector(
    selectFeatureAutoscalingEnabled
  );

  const isGuaranteed = !!resourcesType?.[service?.name];
  const averageUsage = value?.[value?.length - 1]?.["average@percentage"];

  const isHighCpuUsage =
    isSizingApiEnabled &&
    !isGuaranteed &&
    unit === "cpu" &&
    serviceKind?.toLowerCase() === "service" &&
    averageUsage >= 80;

  const resourceConfigUrl = getUrl({
    key: "organization.project.environment.configureResources",
    props: {
      organizationId,
      projectId,
      environmentId,
      location: location.pathname + location.search,
      queryParams: ""
    }
  });

  return (
    <>
      {isHighCpuUsage && (
        <Dialog.Root>
          <Dialog.Trigger analyticId="metrics.tooltip.cpu.warning.title">
            <IconControl>
              <WarningIcon color="var(--theme-warning-600)" />
            </IconControl>
          </Dialog.Trigger>
          <Dialog.Content>
            <Dialog.Title>
              {intl.formatMessage({
                id: "metrics.tooltip.cpu.warning.title"
              })}
            </Dialog.Title>
            <DialogDescription>
              {intl.formatMessage({
                id: "metrics.tooltip.cpu.warning.description"
              })}
            </DialogDescription>
            <Link to={resourceConfigUrl}>
              {intl.formatMessage({ id: "configure_resources" })}
            </Link>
          </Dialog.Content>
        </Dialog.Root>
      )}
      {thresholdsEnabled && unit === "cpu" && autoscalingEnabledFlag && (
        <AutoscalingIcon />
      )}
      {isGuaranteed && unit === "cpu" && <GuaranteedIcon />}
    </>
  );
};

export default ResourceInfo;
